import React, { FC, useMemo, useRef, useState, useEffect, useCallback } from 'react';

import { CardSchema } from 'constants/graphqlTypes';
import UILink from 'components/UI/Link';
import { getGradientImage } from 'components/Sparks/constants';
import { transformRoute } from 'helpers/routingHelper';
import { ROUTE_SPARK_DETAIL } from 'routes';
import { COLORS } from 'styles/constants';

import { formatDistanceToNowStrict } from 'date-fns';
import SparkFooterMenu from './SparkFooterMenu';

import ScatteredImages from './ScatteredImages';
import SparkCardHeader from './SparkCardHeader';
import {
  StyledSparkCard,
  StyledCardBodyClipWrapper,
  StyledCardBodyWrapper,
  StyledCardBody,
  StyledCardInner,
  StyledCardVideo,
  StyledGradientOverlay,
  StyledFooter,
  StyledEmptyState,
  StyledNoiseOverlay,
  StyledEmptyCard,
  StyledEmptyCardLabel,
  StyledCardboardTexture,
} from './styled';

interface SparkCardProps {
  card: CardSchema;
  isDark?: boolean;
  fontColor?: string; // Explicit font color - overrides isDark calculation
  minimal?: boolean; // Controls footer visibility only
  hideHeader?: boolean; // Explicitly hide the header
  showScatteredImages?: boolean;
  maxImages?: number;
  isFirstItem?: boolean;
  isOtherUser?: boolean; // Whether we're viewing another user's spark (enables reaction button)
  isTabReady?: boolean; // Whether the parent tab's slide transition is complete
  expandedHeader?: boolean; // Use expanded header with larger avatar and bio (Featured/Following feeds)
  showFooterMenu?: boolean; // Show CardFooterMenu with action buttons (pick/thoughts/send)
}

// Animation timing constants (in ms)
const CARD_ANIMATION_DURATION = 700;
const SCATTERED_IMAGES_ANIMATION_DURATION = 800; // 500ms animation + 300ms max stagger delay

const SparkCard: FC<SparkCardProps> = ({
  card,
  isDark = true,
  fontColor: fontColorProp,
  minimal = false,
  hideHeader = false,
  showScatteredImages = true,
  maxImages = 4,
  isFirstItem = false,
  isOtherUser: _isOtherUser = false,
  isTabReady = true,
  expandedHeader = false,
  showFooterMenu = false,
}) => {
  // Use explicit fontColor if provided, otherwise derive from isDark
  const fontColor = fontColorProp ?? (isDark ? COLORS.white[100] : COLORS.brown.dark[100]);
  const cardBodyRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasTriggered = useRef(false);

  // Animation state phases
  const [isCardAnimating, setIsCardAnimating] = useState(false);
  const [showCardboardTexture, setShowCardboardTexture] = useState(true);
  const [isHeaderAnimating, setIsHeaderAnimating] = useState(false);
  const [isFooterAnimating, setIsFooterAnimating] = useState(false);
  const [isScatteredImagesAnimating, setIsScatteredImagesAnimating] = useState(false);
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const [isPlaceholderHidden, setIsPlaceholderHidden] = useState(false);
  const [isCardVisible, setIsCardVisible] = useState(false);

  const { user, cardStyle, description, sparkResponses, createdAt, extraData } = card;

  // Extract thought from card description first,
  // then fall back to extraData (user's spark-specific comment)
  const extraDataItem = extraData?.items?.[0] as { text?: string } | undefined;
  const thought = description || extraDataItem?.text || null;

  // Timestamp fallback: elapsed time since creation (e.g. "3 hours ago")
  // Always computed so the header can use it when no thought is present
  const timestamp = createdAt
    ? formatDistanceToNowStrict(new Date(createdAt), { addSuffix: true, roundingMethod: 'floor' })
    : null;

  // Get response cards with images - defined early so we can use in useEffect
  const sparkResponseItems = sparkResponses?.items;
  const responseCards = useMemo(() => {
    return sparkResponseItems ?? [];
  }, [sparkResponseItems]);

  const hasResponses = responseCards.length > 0;

  // Header is shown unless explicitly hidden
  const showHeader = !hideHeader;

  // Start the animation sequence
  const startAnimationSequence = useCallback(() => {
    if (hasTriggered.current) return;
    hasTriggered.current = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Phase 1: Start card animation
        setIsCardAnimating(true);

        // Phase 2: Start header animation (if showing header)
        if (showHeader) {
          setTimeout(() => {
            setIsHeaderAnimating(true);
          }, 100); // Slight delay after card starts
        }

        // Phase 3: Fade out cardboard texture (0.2s delay, then 0.3s fade)
        setTimeout(() => {
          setShowCardboardTexture(false);
        }, 200);

        // Phase 4: Footer fade in (after card animation)
        setTimeout(() => {
          setIsFooterAnimating(true);
        }, CARD_ANIMATION_DURATION);

        // Phase 5: Scattered images animation (after card animation completes)
        setTimeout(() => {
          setIsScatteredImagesAnimating(true);
        }, CARD_ANIMATION_DURATION);

        // Phase 6: Start video playback (after scattered images animation completes)
        setTimeout(() => {
          setShouldPlayVideo(true);
        }, CARD_ANIMATION_DURATION + SCATTERED_IMAGES_ANIMATION_DURATION);

        // Phase 7: Hide placeholder after full animation sequence
        setTimeout(() => {
          setIsPlaceholderHidden(true);
        }, CARD_ANIMATION_DURATION + SCATTERED_IMAGES_ANIMATION_DURATION + 300); // Extra 300ms buffer
      });
    });
  }, [showHeader]);

  // IntersectionObserver to trigger animation when 80% of card is visible AND tab is ready
  useEffect(() => {
    const cardBody = cardBodyRef.current;
    if (!cardBody || hasTriggered.current || !isTabReady) return undefined;

    // If first item, trigger immediately when tab is ready
    if (isFirstItem) {
      requestAnimationFrame(() => {
        startAnimationSequence();
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            startAnimationSequence();
          }
        });
      },
      {
        threshold: 0.8, // Trigger when 80% visible
      }
    );

    // Use requestAnimationFrame to ensure the element is properly laid out
    requestAnimationFrame(() => {
      if (hasTriggered.current) return;

      const rect = cardBody.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Guard against zero height (element not yet laid out)
      if (rect.height === 0) {
        observer.observe(cardBody);
        return;
      }

      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const visibilityRatio = visibleHeight / rect.height;

      if (visibilityRatio >= 0.8 && rect.top < viewportHeight && rect.bottom > 0) {
        // Already 80% visible, trigger animation
        startAnimationSequence();
      } else {
        // Not visible yet, observe for intersection
        observer.observe(cardBody);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isFirstItem, isTabReady, startAnimationSequence]);

  // IntersectionObserver to track visibility for video play/pause (continuous, not one-time)
  useEffect(() => {
    const cardBody = cardBodyRef.current;
    if (!cardBody) return undefined;

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsCardVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.8, // 80% visible
      }
    );

    visibilityObserver.observe(cardBody);

    return () => {
      visibilityObserver.disconnect();
    };
  }, []);

  // Type assertion for cover selection
  const typedCard = card as typeof card & {
    userCoverSelection?: {
      mediaUrl?: string;
      mediaType?: string;
      thumbnailUrl?: string;
      gradientStart?: string;
      gradientEnd?: string;
    };
  };

  const coverMediaUrl = typedCard.userCoverSelection?.mediaUrl;
  const thumbnailUrl = typedCard.userCoverSelection?.thumbnailUrl;
  const isAnimatedCover = typedCard.userCoverSelection?.mediaType === 'ANIMATION';
  const gradientStart = typedCard.userCoverSelection?.gradientStart;
  const gradientEnd = typedCard.userCoverSelection?.gradientEnd;
  const predefinedGradientName = cardStyle?.bgColor?.predefinedGradientName;

  // Background: thumbnail for animations, cover for images, or gradient fallback
  const backgroundImage = useMemo(() => {
    if (isAnimatedCover && thumbnailUrl) {
      return thumbnailUrl;
    }
    if (coverMediaUrl && !isAnimatedCover) {
      return coverMediaUrl;
    }
    return getGradientImage(predefinedGradientName ?? '');
  }, [isAnimatedCover, thumbnailUrl, coverMediaUrl, predefinedGradientName]);

  // Play/pause video based on visibility and animation state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (shouldPlayVideo && isCardVisible) {
      video.play().catch(() => {
        // Video play failed (e.g., autoplay blocked), ignore
      });
    } else if (!isCardVisible && !video.paused) {
      video.pause();
    }
  }, [shouldPlayVideo, isCardVisible]);

  if (!user) {
    return null;
  }

  return (
    <StyledSparkCard>
      {/* Header with avatar, thought/date, and reaction menu - only for other users' sparks */}
      {showHeader && (
        <SparkCardHeader
          user={user}
          text={thought}
          timestamp={timestamp}
          pickId={card.pickId || card.id}
          fontColor={fontColor}
          isAnimating={isHeaderAnimating}
          expanded={expandedHeader}
        />
      )}

      {/* Card body with cover and scattered images */}
      <StyledCardBodyClipWrapper>
        <UILink route={transformRoute(ROUTE_SPARK_DETAIL, { sparkId: card.id })}>
          <StyledCardBodyWrapper ref={cardBodyRef}>
            {/* Placeholder card - visible until animation sequence completes */}
            <StyledEmptyCard isHidden={isPlaceholderHidden} isDark={isDark}>
              <StyledEmptyCardLabel isDark={isDark}>Spark</StyledEmptyCardLabel>
            </StyledEmptyCard>

            {/* Animated card content */}
            <StyledCardBody isCardAnimating={isCardAnimating}>
              {/* Scattered response images - rendered BEHIND the cover */}
              {showScatteredImages && hasResponses && (
                <ScatteredImages
                  responses={responseCards}
                  maxImages={maxImages}
                  isAnimating={isScatteredImagesAnimating}
                />
              )}

              {/* Inner container for cover content (on top of scattered images) */}
              <StyledCardInner bg={backgroundImage || undefined}>
                {/* Cardboard texture overlay - fades out during animation */}
                <StyledCardboardTexture isVisible={showCardboardTexture} />

                {/* Gradient overlay for video covers */}
                {isAnimatedCover && gradientStart && gradientEnd && (
                  <StyledGradientOverlay gradientStart={gradientStart} gradientEnd={gradientEnd} />
                )}

                {/* Video cover - playback delayed until after scattered images animation */}
                {isAnimatedCover && coverMediaUrl && (
                  <StyledCardVideo ref={videoRef} src={coverMediaUrl} loop muted playsInline />
                )}

                {/* Noise overlay */}
                <StyledNoiseOverlay />

                {/* Empty state */}
                {!hasResponses && !minimal && <StyledEmptyState>No picks yet</StyledEmptyState>}
              </StyledCardInner>
            </StyledCardBody>
          </StyledCardBodyWrapper>
        </UILink>
      </StyledCardBodyClipWrapper>

      {/* SparkFooterMenu with add/thoughts/send actions */}
      {showFooterMenu && (
        <StyledFooter isAnimating={isFooterAnimating || showFooterMenu}>
          <SparkFooterMenu spark={card} color={fontColor} />
        </StyledFooter>
      )}
    </StyledSparkCard>
  );
};

export default SparkCard;
